import { useEffect, useMemo, useRef } from "react";
import { getSelectedValue, isChecked, toSelectOptions } from "./utils/common";
import "./css/CheckboxField.css";

export default function CheckboxField({
  id,
  name,
  className,
  label,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  value,
  onChange,
  options,
  valueAttr,
  labelAttr,
  clearLabel,
  isInvalid,
  error,
  help,
  labelStyle,
  disabled,
  ...props
}) {
  const inputRef = useRef();
  const handleChange = (event) => {
    if (event.currentTarget.checked) {
      const selected = getSelectedValue(options, event.target.value, valueAttr);
      inputRef.current.value = [...value, selected];
    } else {
      const filtered = value.filter((item) => {
        if (item instanceof Object) {
          return !(item[valueAttr]?.toString() === event.target.value);
        } else {
          return !(item.toString() === event.target.value);
        }
      });
      inputRef.current.value = filtered;
    }
    inputRef.current.name = name;
    inputRef.current.dispatchEvent(
      new Event("selectionChanged", {
        bubbles: true,
        cancelable: true,
        composed: false,
      }),
    );
  };
  const computedOptions = useMemo(() => {
    return toSelectOptions(options, labelAttr, valueAttr);
  }, [options, labelAttr, valueAttr]);
  const computedClassName = [
    "form-group",
    "checkbox-field",
    isInvalid ? "has-error" : "",
    ...className.split(" "),
  ]
    .join(" ")
    .trim();
  const labelClassName = [
    "col",
    (!!xs && `col-xs-${xs}`) || "",
    (!!sm && `col-sm-${sm}`) || "",
    (!!md && `col-md-${md}`) || "",
    (!!lg && `col-lg-${lg}`) || "",
    (!!xl && `col-xl-${xl}`) || "",
    (!!xxl && `col-xxl-${xxl}`) || "",
  ]
    .join(" ")
    .trim();
  useEffect(() => {
    const input = inputRef.current;
    if (!!input) {
      input.addEventListener("selectionChanged", onChange);
      return () => {
        input.removeEventListener("selectionChanged", onChange);
      };
    }
  }, [onChange]);
  return (
    <div className={computedClassName}>
      <label for={id} style={labelStyle} className="control-label">
        {label}
      </label>
      <div className="checkbox row" id={id} ref={inputRef}>
        {computedOptions.map((option, index) => (
          <label key={index} className={labelClassName}>
            <input
              id={`${id}_${index}`}
              type="checkbox"
              value={option.value}
              checked={isChecked(option.value, value, valueAttr)}
              name={`${name}.${index}`}
              onChange={handleChange}
              disabled={disabled}
            />
            {option.label}
          </label>
        ))}
      </div>
      {isInvalid && !!error && (
        <span className="help-block col-lg-12">{error}</span>
      )}
    </div>
  );
}

CheckboxField.defaultProps = {
  options: [],
  className: "",
  value: [],
};
